import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Search, Clock, CheckCircle, AlertTriangle, Phone, MessageSquare,
  ArrowRight, User, Calendar, TrendingUp, Plus, Target, Zap,
  CalendarDays, Shield, PhoneCall, Ban, DollarSign, RotateCcw,
  Lightbulb, ArrowUpRight, Megaphone, FileText,
} from "lucide-react";
import { LogInteractionModal } from "@/components/quick-actions/LogInteractionModal";
import { EmptyState as SharedEmptyState } from "@/components/EmptyState";
import { cn } from "@/lib/utils";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip, CockpitFilterBar, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { isGrowthChannelSource } from "@/lib/growthChannel";
import { format, isPast, isToday, isTomorrow, parseISO, differenceInHours, addDays } from "date-fns";
import { toast } from "sonner";
import type { OpportunityStage } from "@/data/opportunityTypes";
import { calculateFollowUpEffectiveness, type FollowUpEffectiveness } from "@/data/conversionIntelligence";
import {
  getAllFollowUps, getFollowUpsByRep, getOverdueFollowUps,
  getDueTodayFollowUps, getUpcomingFollowUps, getCompletedFollowUps,
  getFollowUpStats, formatOverdueDuration, riskOrder,
  RISK_CONFIG, type EnrichedFollowUp, type FollowUpRiskLevel,
} from "@/data/followUpSelectors";

/* ═══════ TYPES & CONFIG ═══════ */

type RiskLevel = FollowUpRiskLevel;
type TabView = "due_today" | "overdue" | "upcoming" | "completed";

const REASON_OPTIONS = [
  "Initial contact attempt", "Reschedule needed", "Customer requested callback",
  "No show — re-engage", "Quote follow-up", "Price objection recovery",
  "Manager escalation", "Post-appointment check-in", "Custom",
];

const METHOD_OPTIONS: { value: string; label: string; icon: React.ElementType }[] = [
  { value: "call", label: "Call", icon: PhoneCall },
  { value: "text", label: "Text", icon: MessageSquare },
  { value: "call_and_text", label: "Call + Text", icon: Phone },
];

const NEXT_STEP_SUGGESTIONS: Record<string, string> = {
  "Initial contact attempt": "Schedule an appointment if contact was made",
  "No show — re-engage": "Try a different time slot or contact method",
  "Quote follow-up": "Address objections and offer alternative options",
  "Price objection recovery": "Present financing options or adjusted scope",
  "Customer requested callback": "Confirm interest and schedule appointment",
  "Post-appointment check-in": "Close the deal or identify remaining objections",
  "Manager escalation": "Manager review complete — return to rep queue",
  "Reschedule needed": "Confirm new date and send reminder",
};

/* ═══════ ENRICHED TASK ADAPTER ═══════ */

/** Adapts EnrichedFollowUp to the shape expected by the UI (backward compat) */
interface EnrichedTask {
  id: string; opportunityId: string; type: "rep" | "manager" | "none";
  assignedTo: string; dueDate: string; status: "pending" | "completed" | "overdue";
  notes: string; createdAt: string; customerName: string; customerPhone: string;
  dispositionReason?: string; executionMethod?: string; priority?: string;
  suggestedScript?: string; completedAt?: string; outcome?: string;
  effectiveStatus: string; hoursOverdue: number; risk: RiskLevel;
  isDueToday: boolean; isDueTomorrow: boolean;
  dealValue: number; lastAppointmentResult: string | null;
  lastContactDate: string | null; hasConversation: boolean;
  source?: string; isAutomated?: boolean;
  leadSource?: string;
}

function adaptEnrichedFollowUp(e: EnrichedFollowUp, convos: any[], msgs: any[]): EnrichedTask {
  const task = e.task as any;
  // Last contact from conversations
  const convo = convos.find((c: any) => c.linkedLeadId === task.opportunityId || c.linkedQuoteId === task.opportunityId);
  const convoMsgs = convo ? msgs.filter((m: any) => m.conversationId === convo.id) : [];
  const lastMsg = convoMsgs.length > 0 ? convoMsgs.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0] : null;

  return {
    ...task,
    effectiveStatus: e.effectiveStatus,
    hoursOverdue: e.hoursOverdue,
    risk: e.risk,
    isDueToday: e.isDueToday,
    isDueTomorrow: e.isDueTomorrow,
    dealValue: e.dealValue,
    lastAppointmentResult: e.lastAppointmentResult,
    lastContactDate: lastMsg?.timestamp || null,
    hasConversation: !!convo,
    customerName: e.customerName || task.customerName,
    customerPhone: e.customerPhone || task.customerPhone,
    leadSource: e.opportunity?.leadSource,
  };
}

/* ═══════ MAIN COMPONENT ═══════ */

export default function FollowUpsPage() {
  const { followUpTasks, setFollowUpTasks, salesOpportunities, setSalesOpportunities, conversations, messages } = useAppData();
  const { role } = useRole();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [activeTab, setActiveTab] = useState<TabView>("due_today");
  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState({ customerName: "", customerPhone: "", reason: "", notes: "", method: "call", oppId: "" });
  const [nextStepDialog, setNextStepDialog] = useState<string | null>(null);
  const [convertDialog, setConvertDialog] = useState<string | null>(null);
  const [logTask, setLogTask] = useState<EnrichedTask | null>(null);

  const isRepRole = role === "sales";
  const currentRep = isRepRole ? "Mike Johnson" : null;

  // Single enrichment pass via shared selector
  const allFollowUps = useMemo(() =>
    getAllFollowUps({ followUpTasks, salesOpportunities, conversations, messages }),
    [followUpTasks, salesOpportunities, conversations, messages]
  );

  // Adapt to UI shape
  const enriched = useMemo(() =>
    allFollowUps.map((e) => adaptEnrichedFollowUp(e, conversations, messages)),
    [allFollowUps, conversations, messages]
  );

  const effectiveness = useMemo(() =>
    calculateFollowUpEffectiveness(followUpTasks, salesOpportunities),
    [followUpTasks, salesOpportunities]
  );

  const reps = useMemo(() => {
    const s = new Set(enriched.map((t) => t.assignedTo).filter(Boolean));
    return Array.from(s).sort();
  }, [enriched]);

  const baseFiltered = useMemo(() => {
    let result = enriched;
    if (isRepRole && currentRep) result = result.filter((t) => t.assignedTo === currentRep);
    if (repFilter !== "all") result = result.filter((t) => t.assignedTo === repFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((t) =>
        t.customerName.toLowerCase().includes(q) || t.assignedTo.toLowerCase().includes(q) || t.notes.toLowerCase().includes(q)
      );
    }
    return result;
  }, [enriched, search, repFilter, isRepRole, currentRep]);

  const dueToday = useMemo(() => baseFiltered.filter((t) => t.isDueToday && t.effectiveStatus !== "completed").sort((a, b) => riskOrder(a.risk) - riskOrder(b.risk)), [baseFiltered]);
  const overdue = useMemo(() => baseFiltered.filter((t) => t.effectiveStatus === "overdue").sort((a, b) => b.hoursOverdue - a.hoursOverdue), [baseFiltered]);
  const upcoming = useMemo(() => baseFiltered.filter((t) => t.effectiveStatus === "pending" && !t.isDueToday).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()), [baseFiltered]);
  const completed = useMemo(() => baseFiltered.filter((t) => t.effectiveStatus === "completed").sort((a, b) => new Date(b.completedAt || b.dueDate).getTime() - new Date(a.completedAt || a.dueDate).getTime()), [baseFiltered]);

  // Stats via shared selector
  const stats = useMemo(() => getFollowUpStats(allFollowUps), [allFollowUps]);

  /* ═══════ ACTIONS ═══════ */

  function handleComplete(taskId: string) {
    const task = enriched.find((t) => t.id === taskId);
    setFollowUpTasks((prev) =>
      prev.map((t) => t.id === taskId ? { ...t, status: "completed" as const, completedAt: new Date().toISOString() } : t)
    );
    toast.success("Follow-up completed");
    // Show next step suggestion
    if (task?.dispositionReason) {
      setNextStepDialog(taskId);
    }
  }

  function handleReschedule(taskId: string) {
    setFollowUpTasks((prev) =>
      prev.map((t) => t.id === taskId ? { ...t, dueDate: addDays(new Date(), 1).toISOString(), status: "pending" as const } : t)
    );
    toast.success("Follow-up rescheduled to tomorrow");
  }

  function handleConvertToAppointment() {
    if (!convertDialog) return;
    const task = enriched.find((t) => t.id === convertDialog);
    if (!task?.opportunityId) { toast.error("No linked lead"); return; }
    const now = new Date();
    const apptTime = addDays(now, 1);
    setSalesOpportunities((prev) =>
      prev.map((o) => o.id === task.opportunityId ? {
        ...o, stage: "Appointment Scheduled" as OpportunityStage,
        appointmentTime: apptTime.toISOString(), updatedAt: now.toISOString(),
        activities: [...o.activities, {
          id: `act-${Date.now()}`, type: "note" as const,
          description: `Appointment created from follow-up task`,
          createdAt: now.toISOString(), createdBy: task.assignedTo || "Admin",
        }],
      } : o)
    );
    // Complete the follow-up
    setFollowUpTasks((prev) =>
      prev.map((t) => t.id === convertDialog ? { ...t, status: "completed" as const, completedAt: now.toISOString() } : t)
    );
    toast.success("Appointment created and follow-up completed");
    setConvertDialog(null);
  }

  function handleUpdateLeadStage(taskId: string, stage: OpportunityStage) {
    const task = enriched.find((t) => t.id === taskId);
    if (!task?.opportunityId) return;
    const now = new Date().toISOString();
    setSalesOpportunities((prev) =>
      prev.map((o) => o.id === task.opportunityId ? {
        ...o, stage, updatedAt: now,
        activities: [...o.activities, { id: `act-${Date.now()}`, type: "note" as const, description: `Stage updated to ${stage} via follow-up`, createdAt: now, createdBy: task.assignedTo || "Admin" }],
      } : o)
    );
    toast.success(`Lead moved to ${stage}`);
  }

  function handleCreateTask() {
    if (!newTask.customerName.trim()) { toast.error("Customer name required"); return; }
    const now = new Date();
    const task = {
      id: `FU-${Date.now()}`, opportunityId: newTask.oppId, type: "rep" as const,
      assignedTo: currentRep || "Admin", dueDate: addDays(now, 1).toISOString(),
      status: "pending" as const, notes: `${newTask.reason}${newTask.notes ? ` — ${newTask.notes}` : ""}`,
      createdAt: now.toISOString(), disposition: {} as any,
      customerName: newTask.customerName, customerPhone: newTask.customerPhone,
      priority: "medium" as const, executionMethod: newTask.method as any, dispositionReason: newTask.reason,
    };
    setFollowUpTasks((prev) => [...prev, task]);
    toast.success("Follow-up created");
    setShowCreate(false);
    setNewTask({ customerName: "", customerPhone: "", reason: "", notes: "", method: "call", oppId: "" });
  }

  function formatOverdue(hours: number): string {
    return formatOverdueDuration(hours);
  }

  /* ═══════ RENDER ═══════ */

  return (
    <TooltipProvider>
      <CockpitPage>
        {/* ── Masthead ── */}
        <CockpitMasthead
          eyebrow="Sales · Follow-ups"
          title="Follow-Up Revenue Engine"
          right={
            <>
              <HeroMetric label="Total" value={stats.total} hero />
              <HeroDivider />
              <HeroMetric label="Overdue" value={stats.overdueCount} tone={stats.overdueCount > 0 ? "bad" : "default"} />
              <HeroDivider />
              <HeroMetric label="Due Today" value={stats.dueTodayCount} tone={stats.dueTodayCount > 0 ? "warn" : "default"} />
              <HeroDivider />
              <div className="self-center">
                <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> New Follow-Up
                </Button>
              </div>
            </>
          }
        />

        {/* Revenue At Risk + Overdue Alert */}
        {(stats.overdueCount > 0 || stats.totalAtRisk > 0) && (
          <div className="flex gap-3 flex-wrap">
            {stats.totalAtRisk > 0 && (
              <div className="flex items-center gap-2 border border-destructive/30 bg-destructive/5 rounded-lg px-4 py-2.5">
                <DollarSign className="h-4 w-4 text-destructive" />
                <div>
                  <p className="text-sm font-semibold text-destructive">${stats.totalAtRisk.toLocaleString()} at risk</p>
                  <p className="text-[11px] text-muted-foreground">From overdue and high-priority follow-ups</p>
                </div>
              </div>
            )}
            {stats.overdueCount > 0 && (
              <div className="flex items-center gap-2 border border-warning/30 bg-warning/10 rounded-lg px-4 py-2.5">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <div>
                  <p className="text-sm font-semibold text-warning">{stats.overdueCount} overdue · Avg: {formatOverdue(stats.avgOverdueHrs)}</p>
                  {stats.criticalCount > 0 && <p className="text-[11px] text-destructive font-medium">{stats.criticalCount} critical</p>}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs ml-2 border-warning/40" onClick={() => setActiveTab("overdue")}>View</Button>
              </div>
            )}
          </div>
        )}

        {/* KPI Strip */}
        <CockpitKpiStrip
          items={[
            { label: "Due Today", value: stats.dueTodayCount, tone: stats.dueTodayCount > 0 ? "warn" : "default" },
            { label: "Overdue", value: stats.overdueCount, tone: stats.overdueCount > 0 ? "bad" : "default" },
            { label: "Pending", value: stats.pendingCount },
            { label: "Completed", value: stats.completedCount, tone: "good" },
            { label: "Completion Rate", value: `${stats.completionRate}%`, tone: stats.completionRate >= 80 ? "good" : stats.completionRate >= 60 ? "warn" : "bad" },
          ]}
        />

        {/* Follow-Up Effectiveness Intelligence */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-primary" />
              <p className="text-xs font-semibold text-foreground">Follow-Up Effectiveness</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center border rounded-lg p-2.5">
                <p className="text-lg font-bold text-primary">{effectiveness.completionRate}%</p>
                <p className="text-[10px] text-muted-foreground">Completion Rate</p>
              </div>
              <div className="text-center border rounded-lg p-2.5">
                <p className="text-lg font-bold text-success">{effectiveness.followUpToApptRate}%</p>
                <p className="text-[10px] text-muted-foreground">F/U → Appointment</p>
              </div>
              <div className="text-center border rounded-lg p-2.5">
                <p className="text-lg font-bold text-success">{effectiveness.followUpToSaleRate}%</p>
                <p className="text-[10px] text-muted-foreground">F/U → Sale</p>
              </div>
              <div className="text-center border rounded-lg p-2.5">
                <p className="text-lg font-bold">{effectiveness.avgTimeToCompleteHrs > 0 ? `${effectiveness.avgTimeToCompleteHrs}h` : "—"}</p>
                <p className="text-[10px] text-muted-foreground">Avg Time to Complete</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter bar */}
        <CockpitFilterBar
          search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search follow-ups..." />}
          right={!isRepRole ? (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-[170px] h-9"><SelectValue placeholder="All Reps" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                {reps.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : undefined}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabView)}>
          <TabsList>
            <TabsTrigger value="due_today" className="gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Due Today
              {dueToday.length > 0 && <Badge variant="secondary" className="h-5 min-w-[20px] text-[10px] ml-1">{dueToday.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="overdue" className="gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Overdue
              {overdue.length > 0 && <Badge className="h-5 min-w-[20px] text-[10px] ml-1 bg-destructive">{overdue.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="gap-1.5"><Clock className="h-3.5 w-3.5" /> Upcoming</TabsTrigger>
            <TabsTrigger value="completed" className="gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Completed</TabsTrigger>
          </TabsList>

          {([
            { key: "due_today" as const, items: dueToday, empty: "No follow-ups due today", sub: "All caught up!" as string | undefined, icon: Calendar },
            { key: "overdue" as const, items: overdue, empty: "No overdue follow-ups", sub: "Great work!" as string | undefined, icon: CheckCircle },
            { key: "upcoming" as const, items: upcoming, empty: "No upcoming follow-ups", sub: undefined as string | undefined, icon: Clock },
            { key: "completed" as const, items: completed, empty: "No completed follow-ups yet", sub: undefined as string | undefined, icon: CheckCircle },
          ]).map((tab) => (
            <TabsContent key={tab.key} value={tab.key} className="mt-4 space-y-2">
              {tab.items.length === 0 ? (
                <EmptyState icon={tab.icon} text={tab.empty} sub={tab.sub} />
              ) : tab.items.map((t) => (
                <TaskCard key={t.id} task={t}
                  onComplete={handleComplete}
                  onReschedule={handleReschedule}
                  onLog={(task) => setLogTask(task)}
                  onConvertToAppointment={(id) => setConvertDialog(id)}
                  onOpenConversation={() => navigate("/messages")}
                  onUpdateStage={handleUpdateLeadStage}
                  formatOverdue={formatOverdue}
                />
              ))}
            </TabsContent>
          ))}
        </Tabs>

        {/* Rep Accountability (managers only) */}
        {!isRepRole && reps.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4" /> Rep Accountability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-muted-foreground text-left">
                    <th className="px-3 py-2 font-medium">Rep</th>
                    <th className="px-3 py-2 font-medium text-center">Total</th>
                    <th className="px-3 py-2 font-medium text-center">Overdue</th>
                    <th className="px-3 py-2 font-medium text-center">Due Today</th>
                    <th className="px-3 py-2 font-medium text-center">Completed</th>
                    <th className="px-3 py-2 font-medium text-center">Rate</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {reps.map((rep) => {
                      const repTasks = enriched.filter((t) => t.assignedTo === rep);
                      const repOverdue = repTasks.filter((t) => t.effectiveStatus === "overdue").length;
                      const repToday = repTasks.filter((t) => t.isDueToday && t.effectiveStatus !== "completed").length;
                      const repDone = repTasks.filter((t) => t.effectiveStatus === "completed").length;
                      const rate = repTasks.length > 0 ? Math.round((repDone / repTasks.length) * 100) : 0;
                      return (
                        <tr key={rep} className="hover:bg-muted/30">
                          <td className="px-3 py-2.5 font-medium">{rep}</td>
                          <td className="px-3 py-2.5 text-center">{repTasks.length}</td>
                          <td className="px-3 py-2.5 text-center"><span className={cn(repOverdue > 0 && "text-destructive font-medium")}>{repOverdue}</span></td>
                          <td className="px-3 py-2.5 text-center"><span className={cn(repToday > 0 && "text-warning font-medium")}>{repToday}</span></td>
                          <td className="px-3 py-2.5 text-center text-success">{repDone}</td>
                          <td className="px-3 py-2.5 text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Progress value={rate} className="h-1.5 w-14" /><span className="text-xs font-medium">{rate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create Follow-Up</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Link to Lead (optional)</p>
                <Select value={newTask.oppId} onValueChange={(v) => {
                  const opp = salesOpportunities.find((o) => o.id === v);
                  setNewTask((prev) => ({ ...prev, oppId: v, customerName: opp?.primaryName || prev.customerName, customerPhone: opp?.primaryPhone || prev.customerPhone }));
                }}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select lead..." /></SelectTrigger>
                  <SelectContent>
                    {salesOpportunities.filter((o) => o.stage !== "Closed Won" && o.stage !== "Closed Lost").map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.primaryName} — {o.projectType}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Customer Name</p>
                  <Input value={newTask.customerName} onChange={(e) => setNewTask((p) => ({ ...p, customerName: e.target.value }))} className="h-9" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Phone</p>
                  <Input value={newTask.customerPhone} onChange={(e) => setNewTask((p) => ({ ...p, customerPhone: e.target.value }))} className="h-9" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Reason</p>
                <Select value={newTask.reason} onValueChange={(v) => setNewTask((p) => ({ ...p, reason: v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                  <SelectContent>{REASON_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Method</p>
                <div className="flex gap-2">
                  {METHOD_OPTIONS.map((m) => (
                    <button key={m.value} onClick={() => setNewTask((p) => ({ ...p, method: m.value }))}
                      className={cn("flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs transition-all",
                        newTask.method === m.value ? "border-primary bg-primary/5 font-medium" : "hover:bg-muted/50"
                      )}>
                      <m.icon className="h-3.5 w-3.5" /> {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                <Textarea value={newTask.notes} onChange={(e) => setNewTask((p) => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Additional context..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreateTask}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Next Step Suggestion Dialog */}
        <Dialog open={!!nextStepDialog} onOpenChange={() => setNextStepDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Lightbulb className="h-5 w-5 text-warning" /> Suggested Next Step</DialogTitle></DialogHeader>
            {(() => {
              const task = enriched.find((t) => t.id === nextStepDialog);
              const suggestion = task?.dispositionReason ? NEXT_STEP_SUGGESTIONS[task.dispositionReason] : null;
              return (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{suggestion || "Consider scheduling a follow-up appointment or updating the lead stage."}</p>
                  <div className="flex gap-2">
                    {task?.opportunityId && (
                      <>
                        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => { setConvertDialog(task.id); setNextStepDialog(null); }}>
                          <CalendarDays className="h-3 w-3" /> Schedule Appointment
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => { handleUpdateLeadStage(task.id, "Appointment Scheduled"); setNextStepDialog(null); }}>
                          <ArrowUpRight className="h-3 w-3" /> Move to Active
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
            <DialogFooter><Button variant="outline" onClick={() => setNextStepDialog(null)}>Dismiss</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert to Appointment Dialog */}
        <Dialog open={!!convertDialog} onOpenChange={() => setConvertDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Convert to Appointment</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">This will schedule an appointment for tomorrow, update the lead stage, and complete this follow-up.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertDialog(null)}>Cancel</Button>
              <Button onClick={handleConvertToAppointment}>Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Interaction modal — quick-log call/text without leaving the page */}
        <LogInteractionModal
          open={!!logTask}
          onOpenChange={(open) => { if (!open) setLogTask(null); }}
          customerName={logTask?.customerName || ""}
          defaultType={logTask?.executionMethod === "text" ? "Text" : "Phone Call"}
        />
      </CockpitPage>
    </TooltipProvider>
  );
}

/* ═══════ TASK CARD ═══════ */

function TaskCard({ task, onComplete, onReschedule, onLog, onConvertToAppointment, onOpenConversation, onUpdateStage, formatOverdue }: {
  task: EnrichedTask; onComplete: (id: string) => void; onReschedule: (id: string) => void;
  onLog: (task: EnrichedTask) => void;
  onConvertToAppointment: (id: string) => void; onOpenConversation: () => void;
  onUpdateStage: (id: string, stage: OpportunityStage) => void; formatOverdue: (h: number) => string;
}) {
  const riskCfg = RISK_CONFIG[task.risk];
  const isCompleted = task.effectiveStatus === "completed";
  const isOverdue = task.effectiveStatus === "overdue";
  const dueStr = format(parseISO(task.dueDate), "MMM d, h:mm a");
  const methodIcon = task.executionMethod === "call" ? PhoneCall : task.executionMethod === "text" ? MessageSquare : task.executionMethod === "call_and_text" ? Phone : PhoneCall;

  return (
    <Card className={cn("border-l-4 transition-all", riskCfg.border, isCompleted && "opacity-60")}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Name + badges */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-sm font-semibold text-foreground">{task.customerName}</p>
              {!isCompleted && (
                <Badge variant="outline" className={cn("text-[10px] h-5 gap-0.5", riskCfg.color, riskCfg.bg)}>
                  {task.risk === "critical" && <AlertTriangle className="h-3 w-3" />} {riskCfg.label}
                </Badge>
              )}
              {isCompleted && <Badge variant="outline" className="text-[10px] h-5 bg-success/10 text-success border-success/30">Completed</Badge>}
              {task.source === "system" && (
                <Badge variant="outline" className="text-[10px] h-5 bg-primary/5 text-primary/70 border-primary/15 gap-0.5">
                  <Zap className="h-2.5 w-2.5" /> System Generated
                </Badge>
              )}
              {task.type === "manager" && <Badge variant="outline" className="text-[10px] h-5 border-primary/30 text-primary">Manager</Badge>}
              {isGrowthChannelSource(task.leadSource) && (
                <Badge variant="outline" className="text-[10px] h-5 bg-primary/5 text-primary/70 border-primary/15 gap-0.5">
                  <Megaphone className="h-2.5 w-2.5" /> Channel
                </Badge>
              )}
              {task.dealValue > 0 && <Badge variant="outline" className="text-[10px] h-5 gap-0.5"><DollarSign className="h-3 w-3" />${task.dealValue.toLocaleString()}</Badge>}
            </div>

            {/* Context row: reason + last appointment */}
            <div className="flex items-center gap-3 text-xs mb-1 flex-wrap">
              {task.dispositionReason && <span className="font-medium text-foreground/80">{task.dispositionReason}</span>}
              {task.lastAppointmentResult && (
                <span className="text-muted-foreground">Last appt: <span className="font-medium">{task.lastAppointmentResult}</span></span>
              )}
              {task.lastContactDate && (
                <span className="text-muted-foreground">Last contact: {format(parseISO(task.lastContactDate), "MMM d")}</span>
              )}
            </div>

            {/* Meta row */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {isOverdue ? <span className="text-destructive font-medium">Overdue: {formatOverdue(task.hoursOverdue)}</span> : <>Due: {dueStr}</>}
              </span>
              <span className="flex items-center gap-1"><User className="h-3 w-3" /> {task.assignedTo}</span>
              {task.executionMethod && (
                <span className="flex items-center gap-1">{React.createElement(methodIcon, { className: "h-3 w-3" })} {task.executionMethod === "call_and_text" ? "Call + Text" : task.executionMethod === "call" ? "Call" : "Text"}</span>
              )}
            </div>

            {task.notes && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{task.notes}</p>}
            {task.suggestedScript && !isCompleted && (
              <div className="mt-2 border rounded-md px-3 py-2 bg-muted/30">
                <p className="text-[10px] font-medium text-muted-foreground mb-0.5">
                  {task.source === "system" ? "Automation Suggestion — Ready to Send" : "Suggested Script"}
                </p>
                <p className="text-xs text-foreground line-clamp-2">{task.suggestedScript}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0 flex-wrap">
            {!isCompleted && (
              <>
                <Tooltip><TooltipTrigger asChild>
                  <Button size="sm" className="h-7 text-xs gap-1" onClick={() => onComplete(task.id)}><CheckCircle className="h-3 w-3" /> Complete</Button>
                </TooltipTrigger><TooltipContent>Mark as completed</TooltipContent></Tooltip>

                <Tooltip><TooltipTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onLog(task)}><FileText className="h-3 w-3" /> Log</Button>
                </TooltipTrigger><TooltipContent>Log call/text outcome</TooltipContent></Tooltip>

                <Tooltip><TooltipTrigger asChild>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => onReschedule(task.id)}><RotateCcw className="h-3 w-3" /></Button>
                </TooltipTrigger><TooltipContent>Reschedule to tomorrow</TooltipContent></Tooltip>

                {task.opportunityId && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => onConvertToAppointment(task.id)}><CalendarDays className="h-3 w-3" /></Button>
                  </TooltipTrigger><TooltipContent>Convert to appointment</TooltipContent></Tooltip>
                )}

                <Tooltip><TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onOpenConversation}><MessageSquare className="h-3.5 w-3.5" /></Button>
                </TooltipTrigger><TooltipContent>Open conversation</TooltipContent></Tooltip>
              </>
            )}
            {task.opportunityId && (
              <Tooltip><TooltipTrigger asChild>
                <Link to={`/opportunities/${task.opportunityId}`}><Button size="icon" variant="ghost" className="h-7 w-7"><ArrowRight className="h-3.5 w-3.5" /></Button></Link>
              </TooltipTrigger><TooltipContent>View lead</TooltipContent></Tooltip>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════ SHARED ═══════ */

function KPI({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={cn("h-5 w-5 text-muted-foreground", accent)} />
        <div>
          <p className={cn("text-lg font-bold", accent)}>{value}</p>
          <p className="text-[11px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, text, sub }: { icon: React.ElementType; text: string; sub?: string }) {
  return (
    <SharedEmptyState
      icon={Icon as React.ComponentType<{ className?: string }> as never}
      title={text}
      description={sub}
      compact
    />
  );
}

// riskOrder imported from followUpSelectors
